import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Volume2, VolumeX, Check, Plane, MessageCircle, AlertCircle, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_ICONS = {
  client_message: MessageCircle,
  change_request: AlertCircle,
  itinerary_approved: Check,
  card_auth_received: CreditCard,
};

const NOTIFICATION_COLORS = {
  client_message: 'text-[#C5A059]',
  change_request: 'text-[#B37E6B]',
  itinerary_approved: 'text-[#8DA399]',
  card_auth_received: 'text-[#8DA399]',
};

// Play notification chime
const playChime = (audioRef) => {
  if (audioRef.current) {
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  }
};

export function NotificationBell({ onNotificationClick }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const lastCountRef = useRef(0);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/travels/notifications`),
        axios.get(`${API}/travels/notifications/count`)
      ]);
      setNotifications(notifRes.data || []);
      const newCount = countRes.data?.unread_count || 0;
      
      // Play sound if new notifications arrived and sound is enabled
      if (soundEnabled && newCount > lastCountRef.current && lastCountRef.current > 0) {
        playChime(audioRef);
      }
      lastCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [soundEnabled]);

  // Poll for notifications
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await axios.patch(`${API}/travels/notifications/${notification.id}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
    
    setIsOpen(false);
    onNotificationClick?.(notification);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/travels/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Hidden audio element for notification sound */}
      <audio ref={audioRef} preload="auto">
        <source src="/chime.mp3" type="audio/mpeg" />
      </audio>

      {/* Bell Button */}
      <button
        data-testid="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-[#F2F0EB] transition-colors"
      >
        <Bell className="w-5 h-5 text-[#1A1A1A]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#B37E6B] text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-[rgba(0,0,0,0.08)] z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between">
              <h3 className="font-medium text-[#1A1A1A]">Notifications</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="p-1.5 rounded hover:bg-[#F2F0EB] transition-colors"
                  title={soundEnabled ? 'Mute notifications' : 'Enable notification sound'}
                >
                  {soundEnabled ? (
                    <Volume2 className="w-4 h-4 text-[#8C8C8C]" />
                  ) : (
                    <VolumeX className="w-4 h-4 text-[#8C8C8C]" />
                  )}
                </button>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-[#C5A059] hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* Notification List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-[#8C8C8C]">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const Icon = NOTIFICATION_ICONS[notification.notification_type] || Bell;
                  const iconColor = NOTIFICATION_COLORS[notification.notification_type] || 'text-[#8C8C8C]';
                  
                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-[#F9F8F4] transition-colors text-left border-b border-[rgba(0,0,0,0.04)] last:border-0 ${
                        !notification.read ? 'bg-[#F9F8F4]/50' : ''
                      }`}
                    >
                      <div className={`p-2 rounded-lg bg-[#F2F0EB] ${iconColor}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-medium text-[#1A1A1A]' : 'text-[#2C2C2C]'}`}>
                          {notification.message}
                        </p>
                        {notification.trip_title && (
                          <p className="text-xs text-[#8C8C8C] mt-0.5 truncate">
                            <Plane className="w-3 h-3 inline mr-1" />
                            {notification.trip_title}
                          </p>
                        )}
                        <p className="text-xs text-[#8C8C8C] mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-[#C5A059] rounded-full mt-2" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
